import { AnimatePresence, motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { SpinnerGap, Palette, BookmarkSimple, Trash } from "@phosphor-icons/react";
import { FieldLabel, ColorPicker } from "./GraphicsHelpers";

export function ColorPaletteSection({
  brandPalette, brandThemes, themes,
  backgroundColor, setBackgroundColor,
  primaryColor, setPrimaryColor,
  textColor, setTextColor,
  accentColor, setAccentColor,
  useAccent, setUseAccent,
  showThemePanel, setShowThemePanel,
  newThemeName, setNewThemeName,
  savingTheme, onSaveTheme, onLoadTheme, onDeleteTheme,
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <FieldLabel required>Color Palette</FieldLabel>
          {brandPalette && (
            <span className="text-[9px] px-2 py-0.5 rounded-full bg-[#FF4400]/10 text-[#FF4400] font-semibold" data-testid="gfx-brand-palette-badge">
              Brand defaults loaded
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {(brandThemes.length > 0 || themes.length > 0) && (
            <Button
              onClick={() => setShowThemePanel(showThemePanel === true ? false : true)}
              variant="ghost"
              className="h-7 px-2.5 text-[10px] font-semibold text-violet-400 hover:text-violet-300 hover:bg-violet-500/10 rounded-lg gap-1"
              data-testid="gfx-load-theme-btn"
            >
              <Palette size={12} weight="bold" /> Load Theme
            </Button>
          )}
          <Button
            onClick={() => setShowThemePanel(showThemePanel === "save" ? false : "save")}
            variant="ghost"
            className="h-7 px-2.5 text-[10px] font-semibold text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 rounded-lg gap-1"
            data-testid="gfx-save-theme-btn"
          >
            <BookmarkSimple size={12} weight="bold" /> Save Theme
          </Button>
        </div>
      </div>

      {/* Theme Panel */}
      <AnimatePresence>
        {showThemePanel && (
          <motion.div
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden mb-3"
          >
            {showThemePanel === "save" ? (
              <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-3 flex items-center gap-2" data-testid="gfx-save-theme-panel">
                <Input
                  value={newThemeName}
                  onChange={e => setNewThemeName(e.target.value.slice(0, 50))}
                  placeholder="Theme name..."
                  className="bg-[var(--bg-base)] border-[var(--border-strong)] text-sm h-8 flex-1"
                  data-testid="gfx-theme-name-input"
                />
                <Button
                  onClick={onSaveTheme}
                  disabled={savingTheme || !newThemeName.trim()}
                  className="h-8 px-3 bg-emerald-600 hover:bg-emerald-500 text-white text-xs rounded-lg"
                  data-testid="gfx-confirm-save-theme"
                >
                  {savingTheme ? <SpinnerGap size={12} className="animate-spin" /> : "Save"}
                </Button>
                <div className="flex items-center gap-1 ml-1">
                  <div className="w-5 h-5 rounded border border-white/20" style={{ backgroundColor }} title="Background" />
                  <div className="w-5 h-5 rounded border border-white/20" style={{ backgroundColor: primaryColor }} title="Primary" />
                  <div className="w-5 h-5 rounded border border-white/20" style={{ backgroundColor: textColor }} title="Text" />
                  {useAccent && <div className="w-5 h-5 rounded border border-white/20" style={{ backgroundColor: accentColor }} title="Accent" />}
                </div>
              </div>
            ) : (
              <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-3 space-y-1 max-h-64 overflow-y-auto" data-testid="gfx-theme-list">
                {brandThemes.length > 0 && (
                  <>
                    <p className="text-[9px] font-bold uppercase tracking-wider text-[#FF4400] px-2 pt-1 pb-0.5">Brand Themes</p>
                    {brandThemes.map((theme, idx) => (
                      <div key={`brand-${theme.name}-${idx}`} className="flex items-center justify-between p-2 rounded-lg hover:bg-[var(--bg-hover)] transition-colors">
                        <button
                          onClick={() => onLoadTheme(theme)}
                          className="flex items-center gap-2.5 flex-1 text-left"
                          data-testid={`gfx-brand-theme-${idx}`}
                        >
                          <div className="flex items-center gap-0.5">
                            <div className="w-4 h-4 rounded-sm border border-white/10" style={{ backgroundColor: theme.background_color }} />
                            <div className="w-4 h-4 rounded-sm border border-white/10" style={{ backgroundColor: theme.primary_color }} />
                            <div className="w-4 h-4 rounded-sm border border-white/10" style={{ backgroundColor: theme.text_color }} />
                            {theme.use_accent && <div className="w-4 h-4 rounded-sm border border-white/10" style={{ backgroundColor: theme.accent_color }} />}
                          </div>
                          <span className="text-xs font-medium text-[var(--text-primary)]">{theme.name}</span>
                        </button>
                      </div>
                    ))}
                  </>
                )}

                {themes.length > 0 && (
                  <>
                    {brandThemes.length > 0 && <div className="border-t border-[var(--border)] my-1" />}
                    <p className="text-[9px] font-bold uppercase tracking-wider text-violet-400 px-2 pt-1 pb-0.5">Quick Themes</p>
                    {themes.map(theme => (
                      <div key={theme.theme_id} className="flex items-center justify-between p-2 rounded-lg hover:bg-[var(--bg-hover)] transition-colors group">
                        <button
                          onClick={() => onLoadTheme(theme)}
                          className="flex items-center gap-2.5 flex-1 text-left"
                          data-testid={`gfx-theme-${theme.theme_id}`}
                        >
                          <div className="flex items-center gap-0.5">
                            <div className="w-4 h-4 rounded-sm border border-white/10" style={{ backgroundColor: theme.background_color }} />
                            <div className="w-4 h-4 rounded-sm border border-white/10" style={{ backgroundColor: theme.primary_color }} />
                            <div className="w-4 h-4 rounded-sm border border-white/10" style={{ backgroundColor: theme.text_color }} />
                            {theme.use_accent && <div className="w-4 h-4 rounded-sm border border-white/10" style={{ backgroundColor: theme.accent_color }} />}
                          </div>
                          <span className="text-xs font-medium text-[var(--text-primary)]">{theme.name}</span>
                        </button>
                        <button
                          onClick={() => onDeleteTheme(theme.theme_id)}
                          className="p-1 rounded text-[var(--text-muted)] hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                          data-testid={`gfx-delete-theme-${theme.theme_id}`}
                        >
                          <Trash size={12} />
                        </button>
                      </div>
                    ))}
                  </>
                )}

                {themes.length === 0 && brandThemes.length === 0 && (
                  <p className="text-[10px] text-[var(--text-muted)] text-center py-2">No saved themes yet</p>
                )}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Always-visible colors */}
      <div className="grid grid-cols-3 gap-3 mb-3">
        <ColorPicker label="Background" value={backgroundColor} onChange={setBackgroundColor} testId="gfx-color-bg" />
        <ColorPicker label="Primary" value={primaryColor} onChange={setPrimaryColor} testId="gfx-color-primary" />
        <ColorPicker label="Text" value={textColor} onChange={setTextColor} testId="gfx-color-text" />
      </div>

      {/* Accent toggle */}
      <div className="flex items-center gap-3 bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-2.5">
        <Switch
          checked={useAccent}
          onCheckedChange={setUseAccent}
          data-testid="gfx-accent-toggle"
        />
        <span className="text-[11px] font-medium text-[var(--text-secondary)]">
          {useAccent ? "Accent color enabled" : "Add accent color"}
        </span>
        {useAccent && (
          <div className="ml-auto">
            <div className="flex items-center gap-2">
              <input type="color" value={accentColor} onChange={e => setAccentColor(e.target.value)}
                className="w-7 h-7 rounded-lg border-0 cursor-pointer bg-transparent shrink-0 [&::-webkit-color-swatch]:rounded-md [&::-webkit-color-swatch-wrapper]:p-0"
                data-testid="gfx-color-accent" />
              <input type="text" value={accentColor}
                onChange={e => { if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) setAccentColor(e.target.value); }}
                className="bg-transparent text-[11px] font-mono text-[var(--text-secondary)] w-16 outline-none"
                data-testid="gfx-color-accent-hex" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
